import type { Client } from '@efdigital/ll-addon-data';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

function eq(a: string, b: string): boolean {
  return a === b;
}

function normalizeDateInput(value?: string | null): string {
  if (!value) {
    return '';
  }

  const isoDateMatch = value.match(/^\d{4}-\d{2}-\d{2}$/);
  if (isoDateMatch) {
    return isoDateMatch[0];
  }

  if (value.includes('T')) {
    return value.split('T')[0] ?? '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
}

interface Signature {
  Args: {
    client: Client;
    uniqueId: string;
    isReadOnly: boolean;
  };
}

class GeneralSection extends Component<Signature> {
  get siteView(): string {
    return this.args.client.view || 'va-program';
  }

  get isLegacyView(): boolean {
    return (
      this.siteView !== 'va-program' &&
      this.siteView !== 'program' &&
      this.siteView !== 'va'
    );
  }

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.client.title = target.value;
  };

  setMindsetAccountUid = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.client.mindsetAccountUid = target.value;
  };

  setSubscription = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.args.client.subscription = target.value;
  };

  setView = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.client.view = target.value;
  };

  setStarts = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.client.starts = target.value;
  };

  setExpires = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.client.expires = target.value;
  };

  <template>
    <fieldset disabled={{@isReadOnly}} class="mb-4">
      <legend class="gud-eyebrow">Identifier</legend>

      <div class="mb-3">
        <label for="{{@uniqueId}}-title" class="form-label">
          Title
          <span class="text-danger">*</span>
        </label>
        <input
          id="{{@uniqueId}}-title"
          name="title"
          type="text"
          class="form-control"
          value={{@client.title}}
          {{on "change" this.setTitle}}
          required
        />
      </div>
    </fieldset>

    <hr />

    <fieldset disabled={{@isReadOnly}} class="mb-4">
      <legend class="gud-eyebrow">Validity</legend>
      <div class="row mb-3">
        <div class="col-md-6">
          <label for="{{@uniqueId}}-starts" class="form-label">Starts</label>
          <input
            id="{{@uniqueId}}-starts"
            type="date"
            class="form-control"
            value={{normalizeDateInput @client.starts}}
            {{on "change" this.setStarts}}
          />
          <div class="form-text">
            The date that the client's subscription starts. This is used in
            various dashboards and reports.
          </div>
        </div>

        <div class="col-md-6">
          <label for="{{@uniqueId}}-expires" class="form-label">
            Expires
            <span class="text-danger">*</span>
          </label>
          <input
            id="{{@uniqueId}}-expires"
            type="date"
            class="form-control"
            value={{normalizeDateInput @client.expires}}
            {{on "change" this.setExpires}}
            required
          />
          <div class="form-text">
            Users will be denied access after this date.<br />
            You should typically set this to 1 year after the start date.
          </div>
        </div>
      </div>
    </fieldset>

    <hr />

    <fieldset disabled={{@isReadOnly}} class="mb-4">
      <legend class="gud-eyebrow">Features</legend>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">
            Application access
            <span class="text-danger">*</span>
          </label>

          <div class="form-check">
            <input
              name="view"
              id="{{@uniqueId}}-view-va-program"
              type="radio"
              class="form-check-input"
              value="va-program"
              checked={{eq @client.view "va-program"}}
              {{on "change" this.setView}}
            />
            <label
              for="{{@uniqueId}}-view-va-program"
              class="form-check-label"
            >Xceed + Leadership Live</label>
          </div>

          <div class="form-check">
            <input
              name="view"
              id="{{@uniqueId}}-view-program"
              type="radio"
              class="form-check-input"
              value="program"
              checked={{eq @client.view "program"}}
              {{on "change" this.setView}}
            />
            <label
              for="{{@uniqueId}}-view-program"
              class="form-check-label"
            >Xceed only</label>
          </div>

          <div class="form-check">
            <input
              name="view"
              id="{{@uniqueId}}-view-va"
              type="radio"
              class="form-check-input"
              value="va"
              checked={{eq @client.view "va"}}
              {{on "change" this.setView}}
            />
            <label
              for="{{@uniqueId}}-view-va"
              class="form-check-label"
            >Leadership Live only</label>
          </div>

          {{#if this.isLegacyView}}
            <div class="form-check">
              <input
                name="view"
                id="{{@uniqueId}}-view-va-home"
                type="radio"
                class="form-check-input"
                value="va-home"
                checked={{eq @client.view "va-home"}}
              />
              <label
                for="{{@uniqueId}}-view-va-home"
                class="form-check-label"
              >Leadership Live (Simple Home Page)</label>
            </div>
          {{/if}}
        </div>

        <div class="col-md-6 mb-3">
          <label for="{{@uniqueId}}-mindsetAccountUid" class="form-label">
            Mindset AI
          </label>
          <input
            id="{{@uniqueId}}-mindsetAccountUid"
            name="mindsetAccountUid"
            type="text"
            class="form-control font-monospace"
            value={{@client.mindsetAccountUid}}
            {{on "change" this.setMindsetAccountUid}}
          />
          <div class="form-text">
            Leave blank to disable Mindset AI for this client.<br />To enable
            Mindset AI in Leadership Live, enter the client's Mindset Account
            UID here.
          </div>
        </div>
      </div>
    </fieldset>
  </template>
}

export default GeneralSection;
